import i18n from 'i18next';
import { Box, Flex } from 'reflexbox';
import styled from 'styled-components';

import { Paragraph } from '@treams/treams-ui';

export interface DistributionSegment {
	label: string;
	value: number;
	color: string;
}

const BarTrack = styled(Flex)`
	width: 100%;
	height: 4px;
	gap: 1px;
	overflow: hidden;
	border-radius: 2px;
`;

const BarSegment = styled(Box)<{ segmentColor: string }>`
	height: 100%;
	border-radius: 2px;
	background-color: ${(props) => props.segmentColor};
	flex-basis: 0;
	min-width: 4px;
`;

export const LegendSwatch = styled(Box)<{ swatchColor: string }>`
	width: 5px;
	height: 5px;
	background-color: ${(props) => props.swatchColor};
	flex-shrink: 0;
`;

export function Legend({
	segments,
	align = 'start',
	fitContent = false,
}: {
	segments: {
		label?: string;
		progress?: {
			value: number;
			total: number;
		};
		color: string;
	}[];
	align?: 'start' | 'end';
	fitContent?: boolean;
}) {
	const formatPercent = (value: number, total: number) =>
		`${((total > 0 ? value / total : 0) * 100).toLocaleString(i18n.language, {
			maximumFractionDigits: 0,
		})}%`;

	return (
		<Flex width="100%" justifyContent={align === 'end' ? 'flex-end' : 'flex-start'}>
			{segments.map((segment, index) => (
				<Flex
					key={segment.label ?? `${segment.color}-${index}`}
					alignItems="center"
					flex={fitContent ? undefined : 1}
					mr={fitContent ? (index === segments.length - 1 ? 0 : 3) : 2}
				>
					<LegendSwatch swatchColor={segment.color} mr={2} />
					{segment.progress && (
						<>
							<Paragraph small color="antracite" fontWeight="bold">
								{formatPercent(segment.progress.value, segment.progress.total)}
							</Paragraph>
							<Paragraph small color="slateBlue" ml={1}>
								({segment.progress.value}/{segment.progress.total})
							</Paragraph>
						</>
					)}

					{segment.label && (
						<Paragraph small color="antracite" fontWeight="bold">
							{segment.label}
						</Paragraph>
					)}
				</Flex>
			))}
		</Flex>
	);
}

export function CycleMomentDistributionChart({
	segments,
	total,
}: {
	segments: DistributionSegment[];
	total: number;
}) {
	const visibleSegments = segments.filter((segment) => segment.value > 0);

	return (
		<Box width="100%">
			<BarTrack>
				{visibleSegments.map((segment) => (
					<BarSegment
						key={segment.label}
						segmentColor={segment.color}
						style={{ flexGrow: segment.value, borderRadius: 0 }}
					/>
				))}
			</BarTrack>
			<Box mt={2}>
				<Legend
					segments={visibleSegments.map((segment) => ({
						label: undefined,
						color: segment.color,
						progress: {
							value: segment.value,
							total: total,
						},
					}))}
				/>
			</Box>
		</Box>
	);
}
